import { useEffect } from 'react';
import { clsx } from 'clsx';
import {
  ArrowUp,
  ArrowDown,
  CheckCircle2,
  Loader2,
  Play,
  Sparkles,
  XCircle,
} from 'lucide-react';
import { useQualityDashboardStore } from '@/stores/qualityDashboard';
import { useAuth } from '@/contexts/AuthContext';
import type { ThresholdRecommendation } from '@/services/api';

function timeAgo(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

export function ThresholdRecommendationsPanel() {
  const {
    recommendations,
    entityTypeConfigs,
    showAppliedDismissed,
    actingId,
    analyzing,
    analyzeStatus,
    recsLoading,
    fetchRecommendations,
    fetchEntityTypeConfigs,
    setShowAppliedDismissed,
    runAnalysis,
    applyRecommendation,
    dismissRecommendation,
  } = useQualityDashboardStore();
  const { user } = useAuth();
  const reviewerEmail = user?.email ?? undefined;

  useEffect(() => {
    fetchRecommendations();
    fetchEntityTypeConfigs();
  }, [fetchRecommendations, fetchEntityTypeConfigs]);

  const pendingCount = recommendations.filter(
    (r) => !r.applied_at && !r.dismissed_at,
  ).length;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h3 className="text-sm font-semibold text-navy-900">
            Threshold Recommendations
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Output of the weekly verdict-analysis job · {pendingCount} pending
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={showAppliedDismissed}
              onChange={(e) => setShowAppliedDismissed(e.target.checked)}
              className="accent-brand-teal"
            />
            Show applied / dismissed
          </label>
          <button
            onClick={() => runAnalysis(reviewerEmail)}
            disabled={analyzing}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-brand-teal text-navy-900 rounded-lg hover:bg-brand-teal/90 disabled:opacity-40"
          >
            {analyzing ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Play size={11} />
            )}
            Run analysis now
          </button>
        </div>
      </div>

      {analyzeStatus && (
        <div className="text-xs bg-brand-teal/5 border border-brand-teal/10 rounded-lg px-3 py-2 text-brand-teal-dark mb-3">
          {analyzeStatus}
        </div>
      )}

      {recsLoading && recommendations.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-4">
          <Loader2 size={12} className="animate-spin inline mr-1" />
          Loading recommendations...
        </div>
      ) : recommendations.length === 0 ? (
        <div className="text-xs text-slate-400 text-center py-8">
          <Sparkles size={18} className="text-slate-300 mx-auto mb-2" />
          <p>No recommendations yet.</p>
          <p className="mt-1">
            The analysis needs ≥20 human verdicts per entity type before it will
            recommend a threshold change.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {recommendations.map((r) => (
            <Row
              key={r.id}
              rec={r}
              busy={actingId === r.id}
              canAct={!r.applied_at && !r.dismissed_at}
              onApply={() => applyRecommendation(r.id, reviewerEmail)}
              onDismiss={() => dismissRecommendation(r.id, reviewerEmail)}
            />
          ))}
        </div>
      )}

      {/* Current entity type thresholds */}
      {entityTypeConfigs.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">
            Current Thresholds
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {entityTypeConfigs.map((c) => (
              <div
                key={c.entity_type}
                className="flex items-center gap-2 border border-slate-200 rounded-lg px-2 py-1.5"
              >
                <span className="text-slate-700 truncate flex-1">{c.entity_type}</span>
                <span className="font-mono text-navy-900">
                  {c.similarity_threshold.toFixed(2)}
                </span>
                {c.streaming_enabled && (
                  <span className="text-[9px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-1 rounded">
                    streaming
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({
  rec,
  busy,
  canAct,
  onApply,
  onDismiss,
}: {
  rec: ThresholdRecommendation;
  busy: boolean;
  canAct: boolean;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const delta = rec.recommended_threshold - rec.current_threshold;
  const Arrow = delta > 0 ? ArrowUp : ArrowDown;
  return (
    <div
      className={clsx(
        'border rounded-lg p-3',
        rec.applied_at
          ? 'border-emerald-200 bg-emerald-50/40'
          : rec.dismissed_at
            ? 'border-slate-200 bg-slate-50/60'
            : 'border-amber-200 bg-amber-50/40',
      )}
    >
      <div className="flex items-center gap-3 mb-1">
        <span className="text-sm font-semibold text-navy-900">{rec.entity_type}</span>
        <span className="text-xs text-slate-500 font-mono">
          {rec.current_threshold.toFixed(2)}
        </span>
        <Arrow
          size={12}
          className={delta > 0 ? 'text-amber-600' : 'text-emerald-600'}
        />
        <span
          className={clsx(
            'text-xs font-semibold font-mono',
            delta > 0 ? 'text-amber-700' : 'text-emerald-700',
          )}
        >
          {rec.recommended_threshold.toFixed(2)}
        </span>
        <span className="text-[10px] text-slate-500">
          ({delta > 0 ? '+' : ''}
          {delta.toFixed(2)})
        </span>
        {rec.sample_size != null && (
          <span className="text-[10px] text-slate-500">
            n={rec.sample_size}
          </span>
        )}
        {rec.override_rate != null && (
          <span className="text-[10px] text-slate-500">
            override {(rec.override_rate * 100).toFixed(0)}%
          </span>
        )}
        <span className="ml-auto text-[10px] text-slate-400">
          {timeAgo(rec.created_at)}
        </span>
      </div>
      {rec.basis && <p className="text-xs text-slate-700">{rec.basis}</p>}
      <div className="flex items-center gap-2 mt-2">
        {rec.applied_at ? (
          <span className="text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-full uppercase">
            applied {timeAgo(rec.applied_at)}
            {rec.applied_by ? ` by ${rec.applied_by}` : ''}
          </span>
        ) : rec.dismissed_at ? (
          <span className="text-[10px] font-bold bg-slate-100 text-slate-600 border border-slate-200 px-2 py-0.5 rounded-full uppercase">
            dismissed {timeAgo(rec.dismissed_at)}
          </span>
        ) : (
          <>
            <button
              onClick={onApply}
              disabled={busy || !canAct}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:opacity-40"
            >
              {busy ? (
                <Loader2 size={11} className="animate-spin" />
              ) : (
                <CheckCircle2 size={11} />
              )}
              Apply
            </button>
            <button
              onClick={onDismiss}
              disabled={busy || !canAct}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold bg-white border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 disabled:opacity-40"
            >
              <XCircle size={11} />
              Dismiss
            </button>
          </>
        )}
      </div>
    </div>
  );
}
